import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

async function logActivity(businessId: string, type: string, desc: string, entityId: string, userId: string) {
  try { await supabase.from('activity_log').insert({ business_id: businessId, activity_type: type, description: desc, entity_type: 'user', entity_id: entityId, logged_by: userId }); } catch { /* non-critical */ }
}

interface UpdateMemberData {
  first_name?: string;
  last_name?: string;
  role?: string;
  employment_type?: string;
  phone?: string | null;
  email?: string | null;
  hourly_rate?: number | null;
  status?: string;
  preferred_language?: string;
}

export function useEditTeamMember() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTeamMember = useCallback(async (id: string, businessId: string, userId: string, data: UpdateMemberData, desc: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (data.first_name !== undefined && !data.first_name.trim()) { setError('First name is required'); setLoading(false); return false; }
      const { error: err } = await supabase.from('users').update(data).eq('id', id).eq('business_id', businessId);
      if (err) throw err;
      await logActivity(businessId, 'updated', desc, id, userId);
      setLoading(false); return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to update team member'); setLoading(false); return false; }
  }, []);

  const deactivateTeamMember = useCallback(async (id: string, businessId: string, userId: string, desc: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      const { error: err } = await supabase.from('users').update({ status: 'inactive' }).eq('id', id).eq('business_id', businessId);
      if (err) throw err;
      await logActivity(businessId, 'cancelled', desc, id, userId);
      setLoading(false); return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to deactivate member'); setLoading(false); return false; }
  }, []);

  const reactivateTeamMember = useCallback(async (id: string, businessId: string, userId: string, desc: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      const { error: err } = await supabase.from('users').update({ status: 'active' }).eq('id', id).eq('business_id', businessId);
      if (err) throw err;
      await logActivity(businessId, 'updated', desc, id, userId);
      setLoading(false); return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to reactivate member'); setLoading(false); return false; }
  }, []);

  return { updateTeamMember, deactivateTeamMember, reactivateTeamMember, loading, error };
}
